import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '@/stores/index';
import { DrNearestEvent, TargetDirectionEnum } from '@/types/dr';
import { imageResource } from '@/helpers/ResourceHelper';
import { sentEvent } from '@/helpers/GAHelper';
import PATHS from '@/types/navigationPaths';
import { TabButtonType } from '@/molecules/Tabs';
import TabItem from '@/molecules/Tabs/modules/TabItem';
import { getProgramNameFromSite } from '@/helpers/GetProgramName';
import {
  Container,
  Description,
  DescriptionContainer,
  Suggest,
  TabContainer,
  TabsWrapper,
  Title,
  TitleContainer,
  Wrapper,
} from './EnergySavingIdeas.styles';

const EnergySavingIdeas = ({ nearestDrEvent }: { nearestDrEvent: DrNearestEvent }) => {
  const {
    sites: {
      data: { currentSite },
    },
  } = useSelector((state: RootState) => state);

  const { t } = useTranslation('common');
  const programName = getProgramNameFromSite(currentSite);
  const ideas = t<string, string[]>(`programs.${programName}.savingsEventsScreen.ideas`, {
    returnObjects: true,
  });
  const [selectedTab, setSelectedTab] = useState(ideas[0]);
  const suggest = nearestDrEvent.targetDirection === TargetDirectionEnum.loadDecrease ? 'normal' : 'loadShift';
  const savingsAccessibilityLabel = t(
    `screens.main.savingsEvents.savingsEventsScreen.savingIdeas.ideas.${selectedTab}.accessibilityLabel`,
  );
  const textDescription = t(
    `screens.main.savingsEvents.savingsEventsScreen.savingIdeas.ideas.${selectedTab}.description`,
  );
  const textSuggest = t(
    `screens.main.savingsEvents.savingsEventsScreen.savingIdeas.ideas.${selectedTab}.suggest.${suggest}`,
  );

  const handleClick = (value: string) => {
    sentEvent('click', PATHS.SAVING_IDEAS(value));
    setSelectedTab(value);
  };

  return (
    <Wrapper>
      <TitleContainer>
        <Title>{t(`screens.main.savingsEvents.savingsEventsScreen.savingIdeas.title`)}</Title>
      </TitleContainer>
      <Container>
        <TabContainer>
          <TabsWrapper fullWidth>
            {ideas.map((tabName: string) => {
              const imageName = `appliance${tabName.charAt(0).toUpperCase() + tabName.slice(1)}`;
              const active = imageResource(`${imageName}Active`);
              const deactive = imageResource(`${imageName}Deactive`);

              return (
                <TabItem
                  fullWidth
                  key={tabName}
                  selected={tabName === selectedTab}
                  onClick={() => handleClick(tabName)}
                  image={{
                    active: {
                      src: active?.image,
                    },
                    disabled: {
                      src: deactive?.image,
                    },
                  }}
                  tabButtonType={TabButtonType.Icon}
                  padding={selectedTab === tabName ? '10px 10px 17px 10px' : '10px 10px 20px 10px'}
                  ariaLabel={
                    selectedTab === tabName
                      ? `${savingsAccessibilityLabel}, ${textSuggest}`
                      : t(`screens.main.savingsEvents.savingsEventsScreen.savingIdeas.ideas.${tabName}.label`)
                  }
                />
              );
            })}
          </TabsWrapper>
        </TabContainer>
        <DescriptionContainer tabIndex={0}>
          <Description aria-label={savingsAccessibilityLabel}>{textDescription}</Description>
          <Suggest aria-label={textSuggest}>{textSuggest}</Suggest>
        </DescriptionContainer>
      </Container>
    </Wrapper>
  );
};

export default EnergySavingIdeas;
